import React, { useRef } from "react";
import { useTranslation } from "../i18n";
import { m, useScroll, useTransform, useSpring } from "framer-motion";
import {
  ArrowLeft,
  Video,
  Phone,
  MoreVertical,
  Smile,
  Mic,
  FileText,
  Bot,
  Table,
  Zap,
  Link,
  Receipt,
  CheckCheck,
} from "lucide-react";
import { clsx } from "clsx";
import "./WhatsAppAnimationSection.css";

const SCROLL_HEIGHT = "300vh";

export default function WhatsAppAnimationSection() {
  const { t } = useTranslation();
  const containerRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  const smoothProgress = scrollYProgress; // Direct mapping for reliability

  const messages = [
    {
      type: "client",
      content: t("whatsapp_animation.messages.client1"),
      attachment: true,
    },
    {
      type: "client",
      content: t("whatsapp_animation.messages.client2"),
      attachment: true,
    },
    {
      type: "client",
      content: t("whatsapp_animation.messages.client3"),
      attachment: true,
    },
    {
      type: "business",
      content: t("whatsapp_animation.messages.business1"),
      quote: "Factura_Enero.pdf",
    },
    {
      type: "business",
      content: t("whatsapp_animation.messages.business2"),
      quote: "Factura_Febrero.pdf",
    },
    {
      type: "business",
      content: t("whatsapp_animation.messages.business3"),
      quote: "Factura_Marzo.pdf",
    },
  ];

  const steps = [
    {
      icon: FileText,
      title: t("whatsapp_animation.steps.1.title"),
      body: t("whatsapp_animation.steps.1.body"),
    },
    {
      icon: Bot,
      title: t("whatsapp_animation.steps.2.title"),
      body: t("whatsapp_animation.steps.2.body"),
    },
    {
      icon: Table,
      title: t("whatsapp_animation.steps.3.title"),
      body: t("whatsapp_animation.steps.3.body"),
    },
    {
      icon: Zap,
      title: t("whatsapp_animation.steps.4.title"),
      body: t("whatsapp_animation.steps.4.body"),
    },
    {
      icon: Link,
      title: t("whatsapp_animation.steps.5.title"),
      body: t("whatsapp_animation.steps.5.body"),
    },
    {
      icon: Receipt,
      title: t("whatsapp_animation.steps.6.title"),
      body: t("whatsapp_animation.steps.6.body"),
    },
  ];

  return (
    <section className="relative bg-gray-50 border-t border-gray-200">
      {/* 1. Header (Static) */}
      <div className="pt-24 pb-10 text-center px-4 relative z-10 bg-gray-50">
        <m.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="inline-block p-2 rounded-2xl bg-green-100 text-green-700 mb-4"
        >
          <Bot className="w-8 h-8" />
        </m.div>
        <m.h2
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6"
        >
          {t("whatsapp_animation.title")}
        </m.h2>
        <p className="max-w-xl mx-auto text-gray-600 text-lg">
          {t("whatsapp_animation.scroll_indicator") ||
            "Scroll to see the magic happen"}
        </p>
      </div>

      {/* 2. Scroll Interaction Area */}
      <div
        ref={containerRef}
        className="relative w-full"
        style={{ height: SCROLL_HEIGHT }}
      >
        {/* Sticky Viewport */}
        <div className="sticky top-0 h-screen w-full overflow-hidden">
          <div className="w-full max-w-7xl mx-auto px-4 lg:px-8 h-full relative flex flex-col lg:grid lg:grid-cols-12 items-center lg:items-start lg:gap-12">
            {/* LEFT COLUMN: Phone (Sticky) */}
            <div className="lg:col-span-5 h-full flex flex-col justify-start lg:justify-center items-center lg:items-end w-full relative pt-4 lg:pt-0">
              {/* Mobile: scale down. Desktop: larger */}
              <PhoneMockup messages={messages} progress={smoothProgress} />
            </div>

            {/* RIGHT COLUMN: ZigZag Timeline (Desktop Only) */}
            <div className="hidden lg:flex lg:col-span-7 h-full relative items-center justify-center">
              {/* Timeline Container */}
              <div className="relative w-full h-[80vh] flex items-center justify-center">
                {/* Central Line */}
                <div className="absolute top-0 bottom-0 w-[2px] bg-gray-200 rounded-full left-1/2 -translate-x-1/2 overflow-hidden">
                  <m.div
                    style={{
                      height: useTransform(
                        smoothProgress,
                        [0, 0.9],
                        ["0%", "100%"]
                      ),
                    }}
                    className="w-full bg-primary-500 rounded-full"
                  />
                </div>

                {/* ZigZag Steps */}
                <div className="w-full h-full relative">
                  {steps.map((step, idx) => (
                    <ZigZagStep
                      key={idx}
                      step={step}
                      index={idx}
                      total={steps.length}
                      progress={smoothProgress}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* MOBILE STEPS (Overlay at bottom) */}
            <div className="lg:hidden absolute bottom-12 left-4 right-4 z-30 flex flex-col items-center justify-end h-[20vh] pointer-events-none">
              {steps.map((step, idx) => (
                <MobileStep
                  key={idx}
                  step={step}
                  index={idx}
                  progress={smoothProgress}
                  total={steps.length}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PhoneMockup({ messages, progress }) {
  // Auto-scroll simulation: Move the chat content UP as new messages appear
  // Start scrolling after the first few messages (approx 40% progress)
  // Shift up by ~35% of container height to show the bottom
  const scrollY = useTransform(progress, [0.4, 0.95], ["0%", "-35%"]);

  return (
    <div
      className="relative z-20 shrink-0 transition-transform duration-500 shadow-2xl rounded-[30px] md:rounded-[45px]
       w-[260px] h-[550px]
       lg:w-[340px] lg:h-[700px]
       max-h-[75vh] lg:max-h-auto
    "
    >
      {/* 1. LAYER: Frame Body (z-10) */}
      <div className="absolute inset-0 bg-[#2d2d2d] rounded-[30px] md:rounded-[45px] shadow-2xl z-10 pointer-events-none border-[6px] md:border-[10px] border-[#3d3d3d]" />

      {/* 2. LAYER: Screen (z-20) */}
      <div className="absolute inset-[4px] md:inset-[8px] bg-[#efeae2] rounded-[24px] md:rounded-[36px] overflow-hidden z-20 flex flex-col">
        {/* Header */}
        <div className="bg-[#008069] text-white p-3 pt-8 md:pt-10 pb-2 shadow-md z-10 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <ArrowLeft className="w-5 h-5 cursor-pointer" />
            <img
              src="/W_MINI.png"
              alt="WhappScan"
              className="w-8 h-8 rounded-full object-cover border border-white/20"
            />
            <div className="flex flex-col">
              <span className="font-semibold text-sm leading-tight">
                WhappScan
              </span>
              <span className="text-[10px] opacity-90">Online</span>
            </div>
          </div>
          <div className="flex gap-3 opacity-90">
            <Video className="w-5 h-5" />
            <Phone className="w-5 h-5" />
            <MoreVertical className="w-5 h-5" />
          </div>
        </div>

        {/* Chat */}
        <div className="flex-1 overflow-hidden p-3 flex flex-col gap-2 relative bg-[url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')] bg-repeat bg-[length:300px]">
          <div className="absolute inset-0 opacity-10 bg-white" />
          <m.div
            style={{ y: scrollY }}
            className="relative z-10 flex flex-col gap-2 pb-12 w-full"
          >
            {messages.map((msg, idx) => (
              <MessageBubble
                key={idx}
                message={msg}
                show={progress}
                triggerIndex={idx}
              />
            ))}
          </m.div>
        </div>

        {/* Input */}
        <div className="bg-white p-2 flex items-center gap-2 z-10 border-t border-gray-100 shrink-0">
          <Smile className="text-gray-400 w-6 h-6" />
          <div className="flex-1 bg-gray-100 rounded-full h-10 px-4 flex items-center text-gray-400 text-sm">
            Type a message
          </div>
          <Mic className="text-gray-400 w-6 h-6" />
        </div>
      </div>

      {/* 3. LAYER: Notch (z-30) */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[90px] md:w-[120px] h-[20px] md:h-[28px] bg-black rounded-b-2xl z-30 pointer-events-none" />
    </div>
  );
}

function MessageBubble({ message, show, triggerIndex }) {
  const start = triggerIndex / 6.0;
  const end = start + 0.1;
  const opacity = useTransform(show, [start, end], [0, 1]);
  const y = useTransform(show, [start, end], [20, 0]);
  const scale = useTransform(show, [start, end], [0.8, 1]);

  return (
    <m.div
      style={{ opacity, y, scale }}
      className={clsx(
        "max-w-[85%] rounded-lg p-2 md:p-3 shadow-sm text-xs md:text-sm relative",
        message.type === "business"
          ? "bg-white self-start rounded-tl-none text-gray-800"
          : "bg-[#d9fdd3] self-end rounded-tr-none text-gray-900"
      )}
    >
      {message.quote && (
        <div className="bg-black/5 p-1 px-2 border-l-2 border-primary-500 mb-1 rounded text-[10px] text-gray-500 truncate">
          {message.quote}
        </div>
      )}
      <p>{message.content}</p>
      <div className="flex justify-end gap-1 mt-1 opacity-50 text-[10px]">
        <span>10:{30 + triggerIndex}</span>
        {message.type === "client" && (
          <CheckCheck className="w-3 h-3 text-blue-500" />
        )}
      </div>
    </m.div>
  );
}

function ZigZagStep({ step, index, total, progress }) {
  const isLeft = index % 2 === 0;
  const start = index / total;
  const opacity = useTransform(progress, [start, start + 0.1], [0, 1]);
  const x = useTransform(
    progress,
    [start, start + 0.1],
    isLeft ? [50, 0] : [-50, 0]
  );

  const top = `${(index + 0.5) * (100 / 6)}%`;

  return (
    <m.div
      style={{
        top: top,
        left: isLeft ? "0%" : "50%",
        right: isLeft ? "50%" : "0%",
        opacity: opacity,
        x: x,
      }}
      className={clsx(
        "absolute flex items-center px-8 w-1/2",
        isLeft
          ? "flex-row-reverse text-right justify-end pr-12"
          : "flex-row text-left justify-start pl-12"
      )}
    >
      <div
        className={clsx(
          "absolute top-1/2 w-8 h-[2px] bg-primary-200",
          isLeft ? "right-[2px]" : "left-[2px]"
        )}
      />

      <div
        className={clsx(
          "absolute top-1/2 w-4 h-4 rounded-full border-2 border-white shadow-sm z-10 bg-primary-500",
          isLeft
            ? "right-[-6px] -translate-y-1/2"
            : "left-[-6px] -translate-y-1/2"
        )}
      />

      <div className="bg-white/90 backdrop-blur-md p-5 rounded-2xl shadow-lg border border-gray-100 max-w-xs relative hover:scale-105 transition-transform">
        <div
          className={clsx(
            "mb-2 inline-flex p-2 rounded-lg bg-primary-50 text-primary-600",
            isLeft && "ml-auto"
          )}
        >
          <step.icon className="w-5 h-5" />
        </div>
        <h3 className="font-bold text-gray-900 mb-1">{step.title}</h3>
        <p className="text-sm text-gray-500 leading-relaxed">{step.body}</p>
      </div>
    </m.div>
  );
}

function MobileStep({ step, index, progress, total }) {
  const start = index / total;
  const peak = start + 1 / total / 2;
  const end = (index + 1) / total;
  const opacity = useTransform(progress, [start, peak, end], [0, 1, 0]);
  const y = useTransform(progress, [start, peak, end], [20, 0, -20]);

  return (
    <m.div
      style={{ opacity, y, position: "absolute", bottom: 0 }}
      className="w-full max-w-sm bg-white/90 backdrop-blur-md p-4 rounded-2xl shadow-lg border border-gray-100 flex items-center gap-4 mx-4 mb-4"
    >
      <div className="shrink-0 w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600">
        <step.icon className="w-5 h-5" />
      </div>
      <div>
        <h3 className="font-bold text-gray-900 text-sm">{step.title}</h3>
        <p className="text-xs text-gray-600 line-clamp-2 leading-snug">
          {step.body}
        </p>
      </div>
    </m.div>
  );
}
